// Type-only: this module stays import-free at runtime so its pure helpers can
// be unit-tested without pulling in the fragments/three runtime.
import type * as FRAGS from '@thatopen/fragments';

/**
 * The searchable/displayable attributes of one IFC item, keyed by localId.
 * All fields are optional — a pure relationship container typically has none.
 */
export interface ItemInfo {
  /** `Name`, falling back to `LongName`. */
  name?: string;
  /** `IfcGloballyUniqueId` (the `_guid` attribute). */
  guid?: string;
  /** `Tag` — Revit's "Element ID" and the equivalent in other authoring tools. */
  tag?: string;
}

/** Map of localId → the item's name/guid/tag. */
export type ItemInfoMap = ReadonlyMap<number, ItemInfo>;

export interface SpatialTreeData {
  tree: FRAGS.SpatialTreeItem;
  items: ItemInfoMap;
}

export interface LoadSpatialTreeOptions {
  /**
   * Called as soon as the spatial structure is available, before the (slow,
   * chunked) item-info map is fetched. Lets a caller render the tree
   * immediately and fill in names afterwards.
   */
  onTree?: (tree: FRAGS.SpatialTreeItem) => void;
  /** Polled between chunks so a caller can abort the attribute fetch on unmount. */
  isCancelled?: () => boolean;
}

/** Chunk size for `getItemsData` — ThatOpen's worker chokes on huge batches. */
const CHUNK = 500;

/**
 * ItemsDataConfig requires relationsDefault even though the type is
 * Partial — without it, ThatOpen's worker hits `.get` on undefined.
 */
const DATA_CONFIG: Partial<FRAGS.ItemsDataConfig> = {
  attributesDefault: true,
  relationsDefault: { attributes: false, relations: false },
};

/** A query made only of digits is matched against `localId` instead of text. */
const NUMERIC_QUERY = /^\d+$/;

/** Read one attribute as a trimmed, non-empty string (or undefined). */
function attrString(data: FRAGS.ItemData, key: string): string | undefined {
  const v = (data[key] as FRAGS.ItemAttribute | undefined)?.value;
  if (typeof v !== 'string') return undefined;
  const trimmed = v.trim();
  return trimmed ? trimmed : undefined;
}

/** Display name of a node, or undefined when the item has no Name/LongName. */
export function itemName(items: ItemInfoMap, localId: number | null | undefined): string | undefined {
  if (localId == null) return undefined;
  return items.get(localId)?.name;
}

/**
 * Walk a spatial-tree subtree and collect every localId encountered
 * (containers + leaves). Container nodes carry localIds in IFC too;
 * `setVisible` ignores ids that have no geometry, so it's safe to
 * pass them all.
 */
export function collectIds(node: FRAGS.SpatialTreeItem, out: number[] = []): number[] {
  if (node.localId !== null && node.localId !== undefined) out.push(node.localId);
  if (node.children) {
    for (const child of node.children) collectIds(child, out);
  }
  return out;
}

/**
 * Splice out grouping containers that have neither a category nor a name —
 * those are pure relationship buckets and would render as "?".
 * Nodes with a name OR a category are kept.
 */
export function flattenUnnamed(
  nodes: FRAGS.SpatialTreeItem[] | undefined,
  items: ItemInfoMap,
): FRAGS.SpatialTreeItem[] {
  if (!nodes) return [];
  const out: FRAGS.SpatialTreeItem[] = [];
  for (const n of nodes) {
    // NOTE: `items` holds an entry for anything with a guid/tag too, so this
    // must test for an actual name — not just map membership.
    const hasName = itemName(items, n.localId) !== undefined;
    if (n.category == null && !hasName) {
      if (n.children?.length) out.push(...flattenUnnamed(n.children, items));
    } else {
      out.push(n);
    }
  }
  return out;
}

/** Tri-state of one row, derived from every localId in its subtree. */
export interface CheckState {
  /** Every localId in the subtree (this node included) is selected. */
  all: boolean;
  /** Some — but not all — are selected. */
  some: boolean;
}

/** Neutral state for nodes that carry no localId at all. */
export const CHECK_NONE: CheckState = { all: false, some: false };

export type CheckMap = WeakMap<FRAGS.SpatialTreeItem, CheckState>;

/**
 * One post-order walk that derives every node's tri-state from `selected`,
 * instead of each row re-scanning its own subtree on every render (O(n·depth)
 * across the tree).
 *
 * `previous` enables structural sharing so memoised rows can skip untouched
 * subtrees: a node keeps its old state object only when **neither it nor any
 * descendant** changed. Reusing on the node's own state alone would be a bug —
 * an ancestor that stays "indeterminate" while something deep inside is ticked
 * would keep its identity, and `memo` would then block the re-render that puts
 * the tick on screen.
 */
export function buildCheckMap(
  root: FRAGS.SpatialTreeItem | null | undefined,
  selected: ReadonlySet<number>,
  previous: CheckMap = new WeakMap(),
): CheckMap {
  const map: CheckMap = new WeakMap();
  if (!root) return map;
  const visit = (node: FRAGS.SpatialTreeItem): { total: number; checked: number; reused: boolean } => {
    let total = 0;
    let checked = 0;
    let childrenReused = true;
    if (node.localId != null) {
      total = 1;
      if (selected.has(node.localId)) checked = 1;
    }
    if (node.children) {
      for (const c of node.children) {
        const sub = visit(c);
        total += sub.total;
        checked += sub.checked;
        if (!sub.reused) childrenReused = false;
      }
    }
    const all = total > 0 && checked === total;
    const some = !all && checked > 0;
    const old = previous.get(node);
    const reused = old !== undefined && old.all === all && old.some === some && childrenReused;
    map.set(node, reused ? (old as CheckState) : { all, some });
    return { total, checked, reused };
  };
  visit(root);
  return map;
}

/**
 * Add/remove `ids` in `selected`. Returns a new set, or `null` when nothing
 * would change (so callers can skip a pointless state update + re-render).
 */
export function applyToggle(selected: ReadonlySet<number>, ids: number[], on: boolean): Set<number> | null {
  if (ids.length === 0) return null;
  const next = new Set(selected);
  let changed = false;
  for (const id of ids) {
    if (on) {
      if (!next.has(id)) {
        next.add(id);
        changed = true;
      }
    } else if (next.delete(id)) {
      changed = true;
    }
  }
  return changed ? next : null;
}

/** Which field of a node satisfied a search query. `null` = no match. */
export type MatchKind = 'name' | 'category' | 'tag' | 'localId' | 'guid';

/**
 * The first field that matches `query`, in precedence order:
 * name → category → tag → localId → guid. The order drives the "matched via
 * an identifier only" badge, so name/category deliberately win.
 *
 * `query` must already be trimmed and lower-cased.
 */
function matchKind(node: FRAGS.SpatialTreeItem, items: ItemInfoMap, query: string): MatchKind | null {
  const info = node.localId != null ? items.get(node.localId) : undefined;
  if (info?.name && info.name.toLowerCase().includes(query)) return 'name';
  if (node.category && node.category.toLowerCase().includes(query)) return 'category';
  if (info?.tag && info.tag.toLowerCase().includes(query)) return 'tag';
  // Digits-only queries are treated as an express-id lookup: exact match or
  // prefix, so typing "123" finds #123 and #12345 but not #4123.
  if (node.localId != null && NUMERIC_QUERY.test(query) && String(node.localId).startsWith(query)) return 'localId';
  // GUIDs are case-sensitive base64-ish, but a lower-cased substring test is
  // good enough for a search box (and lets users paste either case).
  if (info?.guid && info.guid.toLowerCase().includes(query)) return 'guid';
  return null;
}

/**
 * Shared search predicate for both IFC trees (drawer + LandXML export dialog):
 * matches name, IFC class, Tag, GUID and localId.
 *
 * `query` must already be trimmed and lower-cased. An empty query matches.
 */
export function nodeMatchesQuery(node: FRAGS.SpatialTreeItem, items: ItemInfoMap, query: string): boolean {
  if (!query) return true;
  return matchKind(node, items, query) !== null;
}

/**
 * The identifier to show as a badge next to a row's label — set only when the
 * node matched through an identifier rather than its name or IFC class, so
 * the user can see *why* the row is in the result list.
 */
export function idMatchBadge(
  node: FRAGS.SpatialTreeItem,
  items: ItemInfoMap,
  query: string,
): string | undefined {
  if (!query) return undefined;
  switch (matchKind(node, items, query)) {
    case 'tag':
      return items.get(node.localId as number)?.tag;
    case 'localId':
      return `#${node.localId}`;
    case 'guid':
      return items.get(node.localId as number)?.guid;
    default:
      return undefined;
  }
}

/**
 * `title` text for a row: the label plus the identifiers, so express ids and
 * authoring-tool tags are discoverable on hover.
 */
export function nodeTooltip(label: string, node: FRAGS.SpatialTreeItem, items: ItemInfoMap): string {
  if (node.localId == null) return label;
  const tag = items.get(node.localId)?.tag;
  // The drawer already renders "IFCWALL #123" for unnamed leaves — don't
  // repeat the id in that case.
  const id = label.endsWith(`#${node.localId}`) ? '' : ` · #${node.localId}`;
  return `${label}${id}${tag ? ` · ${tag}` : ''}`;
}

/**
 * `getSpatialStructure()` plus the chunked attribute fetch that labels
 * unnamed grouping containers and feeds the id/GUID/tag search. Shared by the
 * IFC drawer tree and the LandXML export dialog's picker so both show
 * identical labels/flattening and search the same way.
 */
export async function loadSpatialTree(
  model: FRAGS.FragmentsModel,
  options: LoadSpatialTreeOptions = {},
): Promise<SpatialTreeData> {
  const { onTree, isCancelled } = options;
  const structure = await model.getSpatialStructure();
  onTree?.(structure);

  // Collect every localId in the tree and pull Name/LongName/_guid/Tag
  // attributes in chunks so unnamed grouping containers can be labelled and
  // identifiers become searchable.
  const ids: number[] = [];
  const walkIds = (n: FRAGS.SpatialTreeItem) => {
    if (n.localId != null) ids.push(n.localId);
    n.children?.forEach(walkIds);
  };
  walkIds(structure);

  const itemMap = new Map<number, ItemInfo>();
  for (let i = 0; i < ids.length && !isCancelled?.(); i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    try {
      const datas = (await model.getItemsData(chunk, DATA_CONFIG)) ?? [];
      for (const d of datas) {
        if (!d) continue;
        const lid = (d._localId as FRAGS.ItemAttribute | undefined)?.value;
        if (typeof lid !== 'number') continue;
        const info: ItemInfo = {};
        const name = attrString(d, 'Name') ?? attrString(d, 'LongName');
        if (name) info.name = name;
        const guid = attrString(d, '_guid');
        if (guid) info.guid = guid;
        const tag = attrString(d, 'Tag');
        if (tag) info.tag = tag;
        if (info.name || info.guid || info.tag) itemMap.set(lid, info);
      }
    } catch (err) {
      console.warn('[fragmentTreeData] getItemsData failed', err);
      break;
    }
  }

  return { tree: structure, items: itemMap };
}

/**
 * Resolve IfcGloballyUniqueIds (and the IFC classes present) for a set of
 * localIds. Ids without a `_guid` (pure relationship containers) are simply
 * skipped, so passing whole subtrees — containers included — is safe.
 * Categories are upper-cased to match `model.getCategories()`.
 *
 * Unlike `loadSpatialTree` — where a failed chunk only costs a few labels —
 * a partial GUID list here would silently narrow (or, via the caller's
 * fallbacks, widen) an export. So a chunk failure aborts the walk and the
 * error is re-thrown once the loop is done: callers get all-or-nothing.
 */
export async function resolveGuids(
  model: FRAGS.FragmentsModel,
  localIds: number[],
): Promise<{ guids: string[]; categories: Set<string> }> {
  const guids: string[] = [];
  const categories = new Set<string>();
  let failure: unknown;
  for (let i = 0; i < localIds.length; i += CHUNK) {
    const chunk = localIds.slice(i, i + CHUNK);
    try {
      const datas = (await model.getItemsData(chunk, DATA_CONFIG)) ?? [];
      for (const d of datas) {
        const g = (d?._guid as FRAGS.ItemAttribute | undefined)?.value;
        if (typeof g === 'string' && g) guids.push(g);
        const c = (d?._category as FRAGS.ItemAttribute | undefined)?.value;
        if (typeof c === 'string' && c) categories.add(c.toUpperCase());
      }
    } catch (err) {
      console.warn('[fragmentTreeData] resolveGuids getItemsData failed', err);
      failure = err;
      break;
    }
  }
  if (failure !== undefined) throw failure instanceof Error ? failure : new Error(String(failure));
  return { guids, categories };
}
